import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, BadgePercent, Save } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { PageTitle, Card, Field, TextInput, TextArea, SelectInput, Btn, ConfirmDelete, Badge } from "@/components/admin/ui";
import { ImageField } from "@/components/admin/Media";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const BLANK = { title: "", subtitle: "", badge: "Limited time", discount_label: "", description: "", terms: "", vehicle_slug: "", image: "", valid_from: "", valid_until: "", cta_label: "Enquire now", cta_link: "/contact", active: true, order: 0 };

const today = () => new Date().toISOString().slice(0, 10);
const liveState = (o) => {
  if (!o.active) return { label: "Off", tone: "slate" };
  const t = today();
  if (o.valid_from && o.valid_from > t) return { label: "Scheduled", tone: "gold" };
  if (o.valid_until && o.valid_until < t) return { label: "Expired", tone: "red" };
  return { label: "Live", tone: "green" };
};

export default function OffersAdmin() {
  const qc = useQueryClient();
  const { data: offers = [], isLoading } = useQuery({ queryKey: ["admin-offers"], queryFn: async () => (await api.get("/admin/offers")).data });
  const { data: vehicles = [] } = useQuery({ queryKey: ["admin-vehicles"], queryFn: async () => (await api.get("/admin/vehicles")).data });
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const [editing, setEditing] = useState(null);

  const invalidate = () => { qc.invalidateQueries({ queryKey: ["admin-offers"] }); qc.invalidateQueries({ queryKey: ["offers"] }); };
  const save = useMutation({
    mutationFn: async ({ id, ...body }) => (id ? await api.put(`/admin/offers/${id}`, body) : await api.post("/admin/offers", body)).data,
    onSuccess: (_, vars) => { toast.success(vars.id ? "Offer updated" : "Offer created"); invalidate(); setEditing(null); },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const remove = useMutation({
    mutationFn: async (id) => (await api.delete(`/admin/offers/${id}`)).data,
    onSuccess: () => { toast.success("Offer deleted"); invalidate(); },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const toggleSection = useMutation({
    mutationFn: async (enabled) => (await api.put("/admin/settings", { ...settings, offers_enabled: enabled })).data,
    onSuccess: (d) => { qc.setQueryData(["settings"], d); qc.invalidateQueries({ queryKey: ["offers"] }); toast.success(d.offers_enabled ? "Offers section is now visible on the website" : "Offers section hidden from the website"); },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const vehicleName = (slug) => vehicles.find((v) => v.slug === slug)?.name || (slug ? slug : "All models");

  return (
    <div data-testid="admin-offers-page">
      <PageTitle
        eyebrow="Marketing"
        title="Offers & promotions"
      >
        <Btn onClick={() => setEditing({ ...BLANK, order: offers.length + 1 })} data-testid="add-offer-button"><Plus className="h-4 w-4" /> New offer</Btn>
      </PageTitle>

      <Card className="mb-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <span className={settings?.offers_enabled !== false ? "flex h-11 w-11 items-center justify-center rounded-md bg-gold/15 text-gold" : "flex h-11 w-11 items-center justify-center rounded-md bg-white/5 text-slate-500"}><BadgePercent className="h-5 w-5" /></span>
            <div>
              <p className="font-medium">Show offers on the website</p>
              <p className="text-sm text-slate-500">Controls the home-page section, the Offers page, the navigation link and model badges.</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-slate-400" data-testid="offers-section-state">{settings?.offers_enabled !== false ? "Visible" : "Hidden"}</span>
            <Switch checked={settings?.offers_enabled !== false} disabled={!settings || toggleSection.isPending} onCheckedChange={(v) => toggleSection.mutate(v)} data-testid="offers-section-switch" />
          </div>
        </div>
      </Card>

      {isLoading ? (
        <Loader2 className="h-6 w-6 animate-spin text-crimson" />
      ) : (
        <div className="overflow-x-auto rounded-md border border-white/10 bg-surface">
          <table className="w-full text-left text-sm" data-testid="admin-offers-table">
            <thead className="border-b border-white/10 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
              <tr>
                <th className="px-5 py-4">Offer</th>
                <th className="px-5 py-4">Model</th>
                <th className="px-5 py-4">Validity</th>
                <th className="px-5 py-4">State</th>
                <th className="px-5 py-4">Active</th>
                <th className="px-5 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {offers.map((o) => {
                const st = liveState(o);
                return (
                  <tr key={o.id} data-testid={`offer-row-${o.id}`} className="hover:bg-white/[0.02]">
                    <td className="px-5 py-4">
                      <p className="font-medium text-white">{o.title}</p>
                      <p className="text-xs text-slate-500">{[o.badge, o.discount_label].filter(Boolean).join(" · ")}</p>
                    </td>
                    <td className="px-5 py-4 text-slate-300">{vehicleName(o.vehicle_slug)}</td>
                    <td className="px-5 py-4 font-mono text-xs text-slate-400">{o.valid_from || "now"} → {o.valid_until || "open"}</td>
                    <td className="px-5 py-4"><Badge tone={st.tone}>{st.label}</Badge></td>
                    <td className="px-5 py-4">
                      <Switch checked={o.active} onCheckedChange={(v) => save.mutate({ ...o, active: v })} data-testid={`offer-active-toggle-${o.id}`} />
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => setEditing(o)} data-testid={`offer-edit-${o.id}`} className="flex h-9 w-9 items-center justify-center rounded-md border border-white/10 text-slate-300 hover:border-white/40 hover:text-white"><Pencil className="h-4 w-4" /></button>
                        <ConfirmDelete title={`Delete “${o.title}”?`} onConfirm={() => remove.mutate(o.id)} testid={`offer-delete-${o.id}`}>
                          <button className="flex h-9 w-9 items-center justify-center rounded-md border border-white/10 text-slate-300 hover:border-crimson hover:text-crimson" data-testid={`offer-delete-${o.id}`}><Trash2 className="h-4 w-4" /></button>
                        </ConfirmDelete>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!offers.length && <tr><td colSpan={6} className="px-5 py-12 text-center text-slate-500">No offers yet. Create your first promotion.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      <OfferDialog offer={editing} vehicles={vehicles} onClose={() => setEditing(null)} onSave={(body) => save.mutate(body)} saving={save.isPending} />
    </div>
  );
}

const OfferDialog = ({ offer, vehicles, onClose, onSave, saving }) => {
  const [form, setForm] = useState(BLANK);
  useEffect(() => { if (offer) setForm({ ...BLANK, ...offer }); }, [offer]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const submit = (e) => {
    e.preventDefault();
    if (form.title.trim().length < 2) return toast.error("Give the offer a title");
    if (form.valid_from && form.valid_until && form.valid_from > form.valid_until) return toast.error("End date must be after the start date");
    onSave({ ...form, order: Number(form.order) || 0 });
  };
  return (
    <Dialog open={!!offer} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] w-[min(96vw,900px)] max-w-none overflow-y-auto border-white/10 bg-cardbg p-0 text-white" data-testid="offer-dialog" data-lenis-prevent>
        <form onSubmit={submit}>
          <DialogHeader className="border-b border-white/10 px-6 py-5">
            <DialogTitle className="font-display text-xl font-bold">{form.id ? "Edit offer" : "New offer"}</DialogTitle>
            <DialogDescription className="text-slate-500">Everything here shows on the website exactly as written.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-5 px-6 py-6 md:grid-cols-2">
            <Field label="Title" className="md:col-span-2"><TextInput value={form.title} onChange={set("title")} placeholder="Anniversary Pricing" data-testid="offer-title-input" required /></Field>
            <Field label="Badge" hint="Short tag e.g. Anniversary, Launch deal"><TextInput value={form.badge} onChange={set("badge")} data-testid="offer-badge-input" /></Field>
            <Field label="Headline saving" hint="Shown large in gold"><TextInput value={form.discount_label} onChange={set("discount_label")} placeholder="Save PKR 300,000" data-testid="offer-discount-input" /></Field>
            <Field label="Subtitle"><TextInput value={form.subtitle} onChange={set("subtitle")} placeholder="JETOUR Dashing" data-testid="offer-subtitle-input" /></Field>
            <Field label="Applies to model" hint="Adds an Offer badge on that model">
              <SelectInput value={form.vehicle_slug} onChange={set("vehicle_slug")} data-testid="offer-vehicle-select">
                <option value="">All models / general</option>
                {vehicles.map((v) => <option key={v.slug} value={v.slug}>{v.name}</option>)}
              </SelectInput>
            </Field>
            <Field label="Starts" hint="Leave empty to start immediately"><TextInput type="date" value={form.valid_from} onChange={set("valid_from")} data-testid="offer-valid-from-input" /></Field>
            <Field label="Ends" hint="Leave empty for open-ended; hides automatically after this date"><TextInput type="date" value={form.valid_until} onChange={set("valid_until")} data-testid="offer-valid-until-input" /></Field>
            <Field label="Description" className="md:col-span-2"><TextArea rows={3} value={form.description} onChange={set("description")} data-testid="offer-description-input" /></Field>
            <Field label="Terms & conditions" className="md:col-span-2"><TextArea rows={2} value={form.terms} onChange={set("terms")} data-testid="offer-terms-input" /></Field>
            <Field label="Button label"><TextInput value={form.cta_label} onChange={set("cta_label")} data-testid="offer-cta-label-input" /></Field>
            <Field label="Button link" hint="/contact, /test-drive?vehicle=dashing or a full URL"><TextInput value={form.cta_link} onChange={set("cta_link")} data-testid="offer-cta-link-input" /></Field>
            <Field label="Image" hint="Optional — defaults to the model's car image" className="md:col-span-2"><ImageField value={form.image} onChange={(v) => setForm((f) => ({ ...f, image: v }))} testid="offer-image" /></Field>
            <Field label="Order"><TextInput type="number" value={form.order} onChange={set("order")} data-testid="offer-order-input" /></Field>
            <Field label="Active">
              <div className="flex h-11 items-center gap-3">
                <Switch checked={!!form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} data-testid="offer-active-switch" />
                <span className="text-sm text-slate-400">{form.active ? "Shown on website (within dates)" : "Hidden"}</span>
              </div>
            </Field>
          </div>
          <div className="flex items-center justify-end gap-3 border-t border-white/10 px-6 py-4">
            <Btn type="button" variant="ghost" onClick={onClose} data-testid="offer-cancel-button">Cancel</Btn>
            <Btn type="submit" disabled={saving} data-testid="offer-save-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save offer</Btn>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
